import { useListFilters, useListPagination } from "../../hooks/useListState.js";
import { useMonthState } from "../../hooks/useMonthState.js";
import { useTabState } from "../../hooks/useTabState.js";
import { PAYMENT_TABS } from "../../lib/tabState.js";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import koKR from "antd/locale/ko_KR";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  RotateCcw,
  Search,
  Send,
} from "lucide-react";
import { AppEmpty } from "../../components/Feedback.jsx";
import { paymentSelectionDisabledReason } from "./paymentSelection.js";
import { billingRecipient } from "./billingRecipient.js";
import { schoolDisplayWithGrade } from "../../utils/schoolDisplay.js";
import { formatStudentContact } from "../../utils/studentContact.js";
import { formatWon } from "../../utils/academy.js";
import { paymentEntryMonth } from "./paymentEntryMonth.js";
import { isManager, safeError } from "../../lib/supabase.js";
import {
  billAction,
  cancelPayment,
  listPayments,
  paymentDetail,
  previewPayments,
  recordOnsite,
  resolveMessage,
  sendPayments,
} from "./paymentApi.js";
const labels = {
  untouched: "처리 전",
  billing: "청구 중",
  paid: "납부 완료",
  retry: "재처리 필요",
};
const reasons = {
  expired: "유효기간 만료",
  destroyed: "청구서 파기",
  payment_cancelled: "결제 취소",
};
const stamp = (value) =>
  value
    ? new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "-";
const periodLabel = (month) => dayjs(`${month}-01`).format("YYYY년 M월");
const sumItems = (items) =>
  items.reduce(
    (sum, item) => sum + (item.selected ? Number(item.amount) : 0),
    0,
  );
function previewRow(student) {
  const recipient = billingRecipient(student.studentName, student.guardian);
  const items = student.items.map((item) => ({
    id: item.monthlyChargeItemId,
    billingMonth: item.billingMonth,
    title: `${item.billingMonth.slice(0, 7)} ${item.itemName}`,
    amount: Number(item.amount),
    selected: item.selectedByDefault,
    billable: item.billable,
  }));
  return {
    key: student.studentId,
    studentName: student.studentName,
    recipientName: recipient.name,
    recipientValid: recipient.valid,
    parentPhone: student.guardian?.isPrimary === false ? "-" : formatStudentContact(student.guardian?.phone || student.guardian?.phoneMasked) || "-",
    billingItems: items,
    amount: sumItems(items),
    operationId: crypto.randomUUID(),
    invoiceId: null,
  };
}
function AppliedMonths({ months = [] }) {
  return (
    <section className="paid-payment-detail-items">
      <div className="paid-payment-detail-section-heading">
        <h3>적용 월별 내역</h3>
      </div>
      <div className="paid-payment-detail-item-list">
        {months.flatMap((month) =>
          month.items.map((item) => (
            <div key={`${month.billingMonth}-${item.monthlyChargeItemId}`}>
              <span>
                {month.billingMonth.slice(0, 7)} · {item.itemName}
              </span>
              <strong>{formatWon(item.amount)}</strong>
            </div>
          )),
        )}
      </div>
    </section>
  );
}
export function PaymentsPage({ store, navigation }) {
  const context = store.context;
  const { message } = App.useApp();
  const manager = isManager(context.role);
  const [month, setStoredMonth] = useMonthState("month:payments", paymentEntryMonth);
  const setMonth = value => { if (value !== month) { setLoading(true); setStoredMonth(value); } };
  const [filters, setFilters] = useListFilters("list:payments:filter", { keyword: "" });
  const keyword = filters.keyword;
  const setKeyword = value => {
    value = value.slice(0, 200);
    if (value === keyword) return;
    setLoading(true);
    setFilters({ keyword: value });
  };
  const [tab, setStoredTab] = useTabState("tab:payments", "all", PAYMENT_TABS);
  const setTab = value => { if (value !== tab) { setLoading(true); setStoredTab(value); } };
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [selection, setSelection] = useState([]);
  const [composer, setComposer] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [destroyTarget, setDestroyTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("퇴원");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const version = useRef(0);
  const detailVersion = useRef(0);
  const routePaymentId = navigation?.route.kind === "payment" ? navigation.route.id : "";
  const routePaymentMonth = routePaymentId ? navigation.route.month : "";
  const load = useCallback(async ({ clearSelection = false } = {}) => {
    const current = ++version.current;
    setLoading(true);
    setError("");
    try {
      const result = await listPayments(month, keyword, tab);
      if (current === version.current) {
        setData(result);
        if (clearSelection) setSelection([]);
      }
    } catch (failure) {
      if (current === version.current) {
        setData({ rows: [], counts: {} });
        setError(failure.message);
      }
    } finally {
      if (current === version.current) setLoading(false);
    }
  }, [month, keyword, tab]);
  useEffect(() => {
    setSelection([]);
  }, [month, tab]);
  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => {
      clearTimeout(timer);
      version.current++;
    };
  }, [load]);
  useEffect(() => {
    detailVersion.current++;
    setComposer(null);
    setPaymentTarget(null);
    setDetailOpen(false);
    setDetail(null);
    setCancelTarget(null);
  }, [month]);
  useEffect(() => {
    if (routePaymentId) openDetail(routePaymentId);
    return () => { detailVersion.current++; };
  }, [routePaymentId, routePaymentMonth]);
  async function run(action) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    setActionError("");
    setDetailError("");
    try {
      await action();
    } catch (failure) {
      setError(failure.message);
      setActionError(failure.message);
      if (detailOpen) setDetailError(failure.message);
      message.error(failure.message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function openDetail(id) {
    if (navigation && routePaymentId !== id) { navigation.open("payment", id, month); return; }
    const current = ++detailVersion.current;
    setDetailId(id);
    setDetail(null);
    setDetailError("");
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const result = await paymentDetail(routePaymentMonth || month, id);
      if (current === detailVersion.current) setDetail(result);
    } catch (failure) {
      if (current === detailVersion.current) setDetailError(failure.message);
    } finally {
      if (current === detailVersion.current) setDetailLoading(false);
    }
  }
  const closeDetail = () => {
    if (routePaymentId) { navigation.close(); return; }
    detailVersion.current++;
    setDetailOpen(false);
    setDetail(null);
  };
  const pagination = useListPagination(`list:payments:page:${month}:${tab}`, data.rows.length, keyword, loading || !!error);
  const rows = data.rows.map((row) => ({
    ...row,
    key: row.studentId,
    student: { id: row.studentId, name: row.studentName },
  }));
  const changeMonth = (offset) =>
    setMonth(dayjs(`${month}-01`).add(offset, "month").format("YYYY-MM"));
  function openComposer() {
    return run(async () => {
      if (error || loading) throw new Error("목록을 다시 확인한 뒤 청구 대상을 선택해 주세요.");
      if (selection.length > 100) throw new Error("한 번에 청구 대상을 100명까지 선택할 수 있습니다.");
      const latest = await listPayments(month, "", "all");
      const available = new Set(latest.rows.filter(row => row.canIssueBill).map(row => row.studentId));
      if (!selection.length || selection.some(id => !available.has(id))) {
        throw new Error("선택한 학생 중 청구할 수 없는 학생이 있습니다. 목록을 새로고침해 확인해 주세요.");
      }
      const preview = await previewPayments(month, selection);
      const received = preview.students.map(student => student.studentId);
      if (received.length !== selection.length || new Set(received).size !== selection.length || selection.some(id => !received.includes(id))) {
        throw new Error("선택한 청구 대상을 모두 확인하지 못했습니다. 다시 확인해 주세요.");
      }
      const billRows = preview.students.map(previewRow);
      setComposer({
        billRows,
        previewStudentKey: billRows[0]?.key,
        expanded: [],
        productName: `${dayjs(`${month}-01`).format("M월")} 수강료`,
        paymentStartDate: dayjs().format("YYYY-MM-DD"),
        paymentDueDate: dayjs().add(10, "day").format("YYYY-MM-DD"),
        messageTemplate: `안녕하세요, ${store.state.academyInfo.name}입니다.\n{청구월} 수강료를 안내드립니다.`,
      });
    });
  }
  function toggleItem(rowKey, id, checked) {
    setComposer((current) => ({
      ...current,
      billRows: current.billRows.map((row) =>
        row.key === rowKey && !row.invoiceId
          ? {
              ...row,
              billingItems: row.billingItems.map((item) =>
                item.id === id ? { ...item, selected: checked } : item,
              ),
              amount: sumItems(
                row.billingItems.map((item) =>
                  item.id === id ? { ...item, selected: checked } : item,
                ),
              ),
            }
          : row,
      ),
    }));
  }
  function send() {
    if (composer.billRows.some((row) => !row.recipientValid)) {
      setError("");
      setActionError("청구 대상의 보호자 관계를 부 또는 모로 설정해 주세요.");
      return;
    }
    return run(async () => {
      if (
        !composer.productName.trim() ||
        !composer.paymentStartDate ||
        !composer.paymentDueDate ||
        composer.paymentStartDate > composer.paymentDueDate ||
        composer.billRows.some((row) => row.amount <= 0)
      )
        throw new Error(
          "청구 품목, 납부 기간, 학생별 선택 항목을 확인해 주세요.",
        );
      const outcomes = await sendPayments(context, month, composer);
      const failed = outcomes.filter((item) => item.status !== "sent");
      await load({ clearSelection: true });
      if (failed.length) {
        setError(
          `${outcomes.length - failed.length}명 발송 완료, ${failed.length}명 발송 실패. 실패한 학생만 다시 시도해 주세요.`,
        );
        setComposer((current) => ({
          ...current,
          billRows: current.billRows.filter((row) =>
            failed.some((item) => item.studentId === row.key),
          ).map((row) => failed.some((item) => item.studentId === row.key && item.errorCode === "RECIPIENT_CHANGED")
            ? { ...row, recipientValid: false } : row),
          failureMessage: failed
            .map(
              (item) =>
                `${composer.billRows.find((row) => row.key === item.studentId)?.studentName}: ${item.message || safeError({ code: item.errorCode }).message}`,
            )
            .join(" / "),
        }));
      } else {
        setComposer(null);
        message.success("청구서를 발송했습니다.");
      }
    });
  }
  function openPayment(row) {
    return run(async () => {
      const preview = await previewPayments(month, [row.studentId]);
      const bill = previewRow(preview.students[0]);
      setPaymentTarget({
        student: row.student,
        items: bill.billingItems,
        operationId: crypto.randomUUID(),
      });
      setPaymentForm({
        amount: bill.amount,
        method: "cash",
        paidAt: dayjs().format("YYYY-MM-DDTHH:mm"),
        approvalNumber: "",
        memo: "",
      });
    });
  }
  function savePayment() {
    return run(async () => {
      if (
        !paymentForm.paidAt ||
        paymentForm.amount !== sumItems(paymentTarget.items) ||
        paymentForm.amount <= 0
      )
        throw new Error(
          "선택 항목 합계와 납부 금액, 납부 일시를 확인해 주세요.",
        );
      await recordOnsite(context, month, paymentTarget, paymentForm);
      setPaymentTarget(null);
      message.success("결제가 완료되었습니다.");
      await load({ clearSelection: true });
    });
  }
  function destroy() {
    setActionError("");
    setDestroyTarget(detail.invoice);
  }
  function saveDestroy() {
    return run(async () => {
      await billAction(context, destroyTarget.invoiceId, "destroy");
      setDestroyTarget(null);
      closeDetail();
      message.success("청구서를 파기했습니다.");
      await load({ clearSelection: true });
    });
  }
  function sync() {
    return run(async () => {
      const invoiceId =
        detail.invoice?.invoiceId ||
        detail.payment?.invoiceId ||
        detail.retry?.invoiceId;
      if (!invoiceId) return;
      await billAction(context, invoiceId, "sync");
      await openDetail(detailId);
      await load({ clearSelection: true });
    });
  }
  function saveCancel() {
    return run(async () => {
      await cancelPayment(
        context,
        cancelTarget.payment,
        cancelReason,
        cancelTarget.operationId,
      );
      setCancelTarget(null);
      closeDetail();
      message.success("전체 결제를 취소했습니다.");
      await load({ clearSelection: true });
    });
  }
  const columns = [
    {
      title: "학생 이름",
      width: 180,
      render: (_, row) => (
        <div>
          <strong>{row.studentName}</strong>
          <div className="meta">{schoolDisplayWithGrade(row.schoolLabel, store.state.academyInfo)}</div>
          {paymentSelectionDisabledReason(row) && (
            <Typography.Text style={{ display: "block", fontSize: 12, color: "#64748b" }} role="note">
              {paymentSelectionDisabledReason(row)}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      title: "납부 상태",
      width: 132,
      align: "center",
      render: (_, row) => (
        <Button
          type="text"
          disabled={row.status === "untouched" || busy}
          onClick={() => openDetail(row.studentId)}
        >
          <Tag
            color={{ billing: "gold", paid: "green", retry: "red" }[row.status]}
          >
            {labels[row.status]}
            {row.detailType ? " ›" : ""}
          </Tag>
        </Button>
      ),
    },
    {
      title: "예상 청구 금액",
      width: 150,
      align: "right",
      className: "payment-amount-column",
      render: (_, row) => (
        <strong>{formatWon(row.expectedInvoiceAmount)}</strong>
      ),
    },
    {
      title: "해당 월 수업료",
      width: 145,
      align: "right",
      render: (_, row) => formatWon(row.currentTuitionAmount),
    },
    {
      title: "연체 월 수",
      width: 120,
      align: "center",
      render: (_, row) =>
        row.overdueMonthCount ? (
          <Popover
            trigger={["click", "hover"]}
            content={
              <div>
                {row.overdueMonths.map((item) => (
                  <div key={item.billingMonth}>
                    {item.billingMonth.slice(0, 7)} ·{" "}
                    {formatWon(item.remainingAmount)}
                  </div>
                ))}
                <strong>합계 {formatWon(row.overdueAmount)}</strong>
              </div>
            }
          >
            <Button type="text" size="small">
              {row.overdueMonthCount}개월 <ChevronDown size={13} />
            </Button>
          </Popover>
        ) : (
          "0개월"
        ),
    },
    {
      title: "연체 금액",
      width: 130,
      align: "right",
      render: (_, row) => formatWon(row.overdueAmount),
    },
    {
      title: "결제 방식",
      width: 110,
      align: "center",
      render: (_, row) =>
        row.status === "paid"
          ? row.paymentMethod === "payssam"
            ? "결제선생"
            : row.paymentMethod === "onsite"
              ? "현장결제"
              : "-"
          : "-",
    },
    {
      title: "현장결제",
      width: 132,
      align: "center",
      render: (_, row) => (
        <Button
          className="payment-onsite-button"
          icon={<CreditCard size={15} />}
          disabled={!manager || !row.canRecordOnsite || busy || loading}
          onClick={() => openPayment(row)}
        >
          현장결제
        </Button>
      ),
    },
  ];
  const previewBill =
    composer?.billRows.find((row) => row.key === composer.previewStudentKey) ||
    composer?.billRows[0];
  const invoice = detail?.invoice;
  const payment = detail?.payment;
  const source =
    detail?.status === "paid"
      ? payment
      : detail?.status === "billing"
        ? invoice
        : detail?.retry;
  const onsite = payment?.paymentMethod === "onsite";
  if (!manager)
    return <Alert type="error" message="납부 정보 조회 권한이 없습니다." />;
  return (
    <div
      className={`page-stack payments-page ${composer ? "payment-batch-confirm-active" : ""}`}
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button onClick={load} disabled={busy}>
              재시도
            </Button>
          }
        />
      )}
      <section className="payment-period-context">
        <div className="payment-month-selector payment-period-selector">
          <Button
            type="text"
            icon={<ChevronLeft size={18} />}
            disabled={busy}
            onClick={() => changeMonth(-1)}
            aria-label="이전 달"
          />
          <DatePicker
            className="payment-month-picker"
            picker="month"
            locale={koKR}
            value={dayjs(`${month}-01`)}
            disabled={busy}
            onChange={(value) => value && setMonth(value.format("YYYY-MM"))}
            format="YYYY년 M월"
            allowClear={false}
            suffixIcon={<CalendarDays size={17} />}
          />
          <Button
            type="text"
            icon={<ChevronRight size={18} />}
            disabled={busy}
            onClick={() => changeMonth(1)}
            aria-label="다음 달"
          />
        </div>
      </section>
      <Card className="academy-management-card payment-surface-shadow payment-management-card">
        <div className="academy-tab-panel payment-list-section">
          <div className="section-toolbar payment-list-heading-toolbar">
            <div>
              <h3>월별 납부 현황</h3>
              <span className="meta">{periodLabel(month)} 납부 대상 학생</span>
            </div>
            <div className="payment-list-filter-bar">
              <Input
                className="payment-list-search"
                prefix={<Search size={15} />}
                value={keyword}
                disabled={busy}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="학생 이름 검색"
                allowClear
              />
              <Button
                className="student-filter-reset"
                icon={<RotateCcw size={14} />}
                onClick={() => setKeyword("")}
                disabled={busy}
              >
                검색 초기화
              </Button>
            </div>
          </div>
          <Segmented
            block
            className="payment-history-navigation payment-management-navigation"
            value={tab}
            disabled={busy}
            onChange={setTab}
            options={["all", "untouched", "billing", "paid", "retry"].map(
              (value) => ({
                value,
                label: (
                  <span>
                    {value === "all" ? "전체" : labels[value]}{" "}
                    <strong>{data.counts[value] ?? "-"}</strong>
                  </span>
                ),
              }),
            )}
          />
          <div
            className={`payment-selection-toolbar${selection.length ? " has-selection" : ""}`}
          >
            <div>
              <span>선택한 학생</span>
              <strong>{selection.length}명</strong>
            </div>
            <Button
              type="primary"
              className="payment-batch-send-trigger"
              icon={<Send size={15} />}
              disabled={!selection.length || busy || loading || Boolean(error)}
              onClick={openComposer}
            >
              청구서 발송
            </Button>
          </div>
          <Table
            className="management-list-table payment-main-table payment-management-table"
            rowKey="key"
            columns={columns}
            dataSource={rows}
            loading={loading}
            rowSelection={{
              selectedRowKeys: selection,
              preserveSelectedRowKeys: true,
              onChange: setSelection,
              getCheckboxProps: (row) => ({
                disabled: !row.canIssueBill || busy || loading,
              }),
            }}
            pagination={pagination}
            scroll={{ x: 1170 }}
            locale={{
              emptyText: (
                <AppEmpty description="조건에 해당하는 학생이 없습니다." />
              ),
            }}
          />
        </div>
      </Card>
      {composer && (
        <section className="payment-batch-confirm-page-shell">
          <div className="payment-batch-confirm-page-header">
            <Button
              type="text"
              icon={<ChevronLeft size={18} />}
              disabled={busy}
              onClick={() => { setComposer(null); setError(""); setActionError(""); }}
            >
              청구서 발송
            </Button>
            <Typography.Title level={4}>
              선택한 {composer.billRows.length}명 청구서 확인
            </Typography.Title>
          </div>
          <Card className="academy-management-card payment-surface-shadow payment-batch-confirm-page-card">
            <div className="payment-batch-confirm-body">
              <section className="payment-batch-preview">
                <div className="payment-batch-preview-head">
                  <strong>청구서 미리보기</strong>
                  <Select
                    value={previewBill?.key}
                    onChange={(key) =>
                      setComposer({ ...composer, previewStudentKey: key })
                    }
                    options={composer.billRows.map((row) => ({
                      value: row.key,
                      label: row.studentName,
                    }))}
                  />
                </div>
                <div className="payment-batch-preview-card">
                  <h3>청구서 납부하기</h3>
                  <section>
                    <strong>청구내역</strong>
                    <div>
                      <span>발급처</span>
                      <b>{store.state.academyInfo.name}</b>
                    </div>
                    <div>
                      <span>품목</span>
                      <b>{composer.productName}</b>
                    </div>
                    <div>
                      <span>금액</span>
                      <b>{formatWon(previewBill?.amount)}</b>
                    </div>
                  </section>
                  <section>
                    <strong>안내 메시지</strong>
                    <p>{previewBill ? resolveMessage(composer.messageTemplate, previewBill, month) : ""}</p>
                  </section>
                  <small>
                    본 청구서는 {composer.paymentDueDate}까지 유효합니다.
                  </small>
                </div>
              </section>
              <div className="payment-batch-editor-card">
                {(composer.failureMessage || actionError) && (
                  <Alert
                    type="error"
                    message={composer.failureMessage || actionError}
                  />
                )}
                <Form.Item label="청구 품목">
                  <Input
                    disabled={
                      busy || composer.billRows.some((row) => row.invoiceId)
                    }
                    value={composer.productName}
                    maxLength={100}
                    onChange={(event) =>
                      setComposer({
                        ...composer,
                        productName: event.target.value,
                      })
                    }
                  />
                </Form.Item>
                <div className="payment-batch-period-grid">
                  {[
                    ["paymentStartDate", "공통 납부 시작일"],
                    ["paymentDueDate", "공통 납부 마감일"],
                  ].map(([key, label]) => (
                    <Form.Item key={key} label={label}>
                      <DatePicker
                        disabled={
                          busy || composer.billRows.some((row) => row.invoiceId)
                        }
                        value={composer[key] ? dayjs(composer[key]) : null}
                        onChange={(value) =>
                          setComposer({
                            ...composer,
                            [key]: value?.format("YYYY-MM-DD") || "",
                          })
                        }
                      />
                    </Form.Item>
                  ))}
                </div>
                <Form.Item label="안내 메시지">
                  <Input.TextArea
                    rows={6}
                    maxLength={500}
                    showCount
                    disabled={
                      busy || composer.billRows.some((row) => row.invoiceId)
                    }
                    value={composer.messageTemplate}
                    onChange={(event) =>
                      setComposer({
                        ...composer,
                        messageTemplate: event.target.value,
                      })
                    }
                  />
                </Form.Item>
                <div className="payment-message-token-buttons">
                  {["{청구월}", "{수강수업}", "{학생명}", "{보호자명}"].map(
                    (token) => (
                      <Button
                        key={token}
                        size="small"
                        disabled={
                          busy || composer.billRows.some((row) => row.invoiceId)
                        }
                        onClick={() =>
                          setComposer({
                            ...composer,
                            messageTemplate: (
                              composer.messageTemplate + token
                            ).slice(0, 500),
                          })
                        }
                      >
                        {token}
                      </Button>
                    ),
                  )}
                </div>
                <section className="payment-batch-recipient-list">
                  <div className="payment-batch-recipient-list-heading">
                    <strong>청구 대상</strong>
                    <Button
                      type="link"
                      onClick={() =>
                        setComposer({
                          ...composer,
                          expanded: composer.expanded.length
                            ? []
                            : composer.billRows.map((row) => row.key),
                        })
                      }
                    >
                      전체 내역 펼치기
                    </Button>
                  </div>
                  <Collapse
                    className="payment-batch-recipient-collapse"
                    bordered={false}
                    activeKey={composer.expanded}
                    onChange={(expanded) =>
                      setComposer({ ...composer, expanded })
                    }
                    items={composer.billRows.map((row) => ({
                      key: row.key,
                      label: (
                        <div className="payment-batch-recipient-summary">
                          <dl className="payment-batch-recipient-fields">
                            <div>
                              <dt>학생</dt>
                              <dd>{row.studentName}</dd>
                            </div>
                            <div>
                              <dt>받는 사람</dt>
                              <dd>{row.recipientName}</dd>
                            </div>
                            <div>
                              <dt>보호자 연락처</dt>
                              <dd>{row.parentPhone}</dd>
                            </div>
                          </dl>
                          <strong>{formatWon(row.amount)}</strong>
                        </div>
                      ),
                      children: (
                        <div className="payment-batch-item-list">
                          {row.billingItems.map((item) => (
                            <Checkbox
                              key={item.id}
                              checked={item.selected}
                              disabled={
                                !item.billable || busy || Boolean(row.invoiceId)
                              }
                              onChange={(event) =>
                                toggleItem(
                                  row.key,
                                  item.id,
                                  event.target.checked,
                                )
                              }
                            >
                              <span>
                                {item.title}
                                {!item.billable ? " (처리 중)" : ""}
                              </span>
                              <strong>{formatWon(item.amount)}</strong>
                            </Checkbox>
                          ))}
                        </div>
                      ),
                    }))}
                  />
                </section>
                <div className="payment-batch-send-footer">
                  <span>발송 건 {composer.billRows.length}건</span>
                  <Button
                    type="primary"
                    icon={<Send size={15} />}
                    loading={busy}
                    onClick={send}
                  >
                    바로 발송하기
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}
      <Modal
        destroyOnHidden
        title="현장결제 등록"
        open={Boolean(paymentTarget)}
        onCancel={() => !busy && setPaymentTarget(null)}
        onOk={savePayment}
        okText="납부 완료 처리"
        cancelText="취소"
        confirmLoading={busy}
        width={560}
        className="onsite-payment-modal"
      >
        <div className="onsite-payment-body">
          {actionError && <Alert type="error" message={actionError} />}
          <div className="onsite-payment-heading">
            <strong>{paymentTarget?.student.name}</strong>
            <Tag color="blue">현장결제</Tag>
          </div>
          <dl className="onsite-payment-summary">
            <div>
              <dt>청구 월</dt>
              <dd>{periodLabel(month)}</dd>
            </div>
          </dl>
          <Form layout="vertical" disabled={busy}>
            <Form.Item label="납부 적용 항목">
              {paymentTarget?.items.map((item) => (
                <div key={item.id}>
                  <Checkbox
                    disabled={!item.billable}
                    checked={item.selected}
                    onChange={(event) => {
                      const items = paymentTarget.items.map((next) =>
                        next.id === item.id
                          ? { ...next, selected: event.target.checked }
                          : next,
                      );
                      setPaymentTarget({
                        ...paymentTarget,
                        items,
                        operationId: crypto.randomUUID(),
                      });
                      setPaymentForm({
                        ...paymentForm,
                        amount: sumItems(items),
                      });
                    }}
                  >
                    {item.title} · {formatWon(item.amount)}
                  </Checkbox>
                </div>
              ))}
            </Form.Item>
            <Form.Item label="납부 금액" required>
              <InputNumber
                value={paymentForm.amount}
                readOnly
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="현장 결제 수단">
              <Select
                value={paymentForm.method}
                options={[
                  { value: "card_terminal", label: "카드 단말기" },
                  { value: "cash", label: "현금" },
                  { value: "bank_transfer", label: "계좌 이체" },
                ]}
                onChange={(method) =>
                  setPaymentForm({ ...paymentForm, method })
                }
              />
            </Form.Item>
            <Form.Item label="납부 일시">
              <Input
                type="datetime-local"
                value={paymentForm.paidAt}
                onChange={(event) =>
                  setPaymentForm({ ...paymentForm, paidAt: event.target.value })
                }
              />
            </Form.Item>
            <Form.Item label="승인번호">
              <Input
                value={paymentForm.approvalNumber}
                onChange={(event) =>
                  setPaymentForm({
                    ...paymentForm,
                    approvalNumber: event.target.value,
                  })
                }
              />
            </Form.Item>
            <Form.Item label="메모">
              <Input.TextArea
                value={paymentForm.memo}
                onChange={(event) =>
                  setPaymentForm({ ...paymentForm, memo: event.target.value })
                }
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
      <Drawer
        destroyOnHidden
        className="payment-detail-drawer"
        title={
          detail?.status === "billing"
            ? "청구서 상세"
            : detail?.status === "retry"
              ? "이전 처리 이력"
              : onsite
                ? "납부 상세"
                : "청구·납부 상세"
        }
        open={detailOpen}
        onClose={() => !busy && closeDetail()}
        loading={detailLoading}
        width={560}
        footer={
          <Flex justify="flex-end" gap={8}>
            {(invoice || payment?.paymentMethod === "payssam") && (
              <Button onClick={sync} loading={busy}>
                상태 조회
              </Button>
            )}
            {invoice?.canDestroy && (
              <Button danger disabled={busy} onClick={destroy}>
                청구서 파기
              </Button>
            )}
            {payment?.canCancel && (
              <Button
                danger
                disabled={busy}
                onClick={() => {
                  setActionError("");
                  setCancelReason("퇴원");
                  setCancelTarget({
                    payment,
                    operationId: crypto.randomUUID(),
                  });
                }}
              >
                결제 취소
              </Button>
            )}
            <Button type="primary" disabled={busy} onClick={closeDetail}>
              확인
            </Button>
          </Flex>
        }
      >
        {detailError && (
          <Alert
            type="error"
            message={detailError}
            action={
              <Button onClick={() => openDetail(detailId)}>재시도</Button>
            }
          />
        )}
        {detail && (
          <div className="paid-payment-detail">
            {onsite && detail.status === "paid" && (
              <Alert type="success" message="결제가 완료되었습니다." />
            )}
            <div className="paid-payment-detail-heading">
              <div>
                <strong>{detail.student.studentName}</strong>
                <span>{detail.student.guardian?.name || "보호자 미등록"}</span>
              </div>
              <Tag>{labels[detail.status]}</Tag>
            </div>
            <dl className="paid-payment-detail-info">
              <div className="paid-payment-detail-info-item is-amount">
                <dt>{detail.status === "paid" ? "납부 금액" : "청구 금액"}</dt>
                <dd>{formatWon(source?.amount ?? source?.totalAmount)}</dd>
              </div>
              {payment && (
                <>
                  <div className="paid-payment-detail-info-item">
                    <dt>납부 일시</dt>
                    <dd>{stamp(payment.paidAt)}</dd>
                  </div>
                  <div className="paid-payment-detail-info-item">
                    <dt>결제 방식</dt>
                    <dd>{onsite ? "현장결제" : "결제선생"}</dd>
                  </div>
                  {onsite && (
                    <>
                      <div className="paid-payment-detail-info-item">
                        <dt>승인번호</dt>
                        <dd>{payment.approvalNumber || "-"}</dd>
                      </div>
                      <div className="paid-payment-detail-info-item">
                        <dt>등록자</dt>
                        <dd>{payment.registeredBy || "-"}</dd>
                      </div>
                    </>
                  )}
                </>
              )}
              {!onsite && source?.sentAt && (
                <>
                  <div className="paid-payment-detail-info-item">
                    <dt>청구서 발송일</dt>
                    <dd>{stamp(source.sentAt)}</dd>
                  </div>
                  <div className="paid-payment-detail-info-item">
                    <dt>납부 가능 기간</dt>
                    <dd>
                      {source.paymentStartDate} ~ {source.paymentDueDate}
                    </dd>
                  </div>
                </>
              )}
              {detail.status === "retry" && (
                <>
                  <div className="paid-payment-detail-info-item">
                    <dt>재처리 사유</dt>
                    <dd>{reasons[detail.retryReason] || "-"}</dd>
                  </div>
                  <div className="paid-payment-detail-info-item">
                    <dt>발생 일시</dt>
                    <dd>{stamp(source?.occurredAt)}</dd>
                  </div>
                  <div className="paid-payment-detail-info-item">
                    <dt>취소·파기 사유</dt>
                    <dd>{source?.operationReason || "-"}</dd>
                  </div>
                </>
              )}
            </dl>
            <AppliedMonths months={source?.appliedMonths} />
            {(invoice?.message || payment?.memo) && (
              <section className="payment-detail-note">
                <h3>{onsite ? "메모" : "안내 메시지"}</h3>
                <p>{invoice?.message || payment?.memo}</p>
              </section>
            )}
            {detail.attempts.length > 0 && (
              <Collapse
                items={detail.attempts.map((attempt) => ({
                  key: attempt.invoiceId,
                  label: `${{ sent: "청구 중", paid: "납부 완료", expired: "유효기간 만료", destroyed: "청구서 파기", payment_cancelled: "결제 취소", draft: "발송 준비 중", discarded: "실패 초안 무효 처리" }[attempt.status] || "처리 이력"} · ${formatWon(attempt.amount)}`,
                  children: (
                    <>
                      <p>
                        {stamp(attempt.occurredAt)} ·{" "}
                        {attempt.operationReason || "-"}
                      </p>
                      <AppliedMonths months={attempt.appliedMonths} />
                    </>
                  ),
                }))}
              />
            )}
          </div>
        )}
      </Drawer>
      <Modal
        destroyOnHidden
        className="payment-destroy-confirm"
        title="청구서 파기"
        open={Boolean(destroyTarget)}
        onCancel={() => !busy && setDestroyTarget(null)}
        onOk={saveDestroy}
        confirmLoading={busy}
        okText="청구서 파기"
        cancelText="취소"
        okButtonProps={{ danger: true }}
      >
        <p>
          {detail?.student.studentName} ·{" "}
          {formatWon(destroyTarget?.totalAmount)}. 파기 후 새 청구서를 발송할 수
          있습니다.
        </p>
        {actionError && <Alert type="error" message={actionError} />}
      </Modal>
      <Drawer
        destroyOnHidden
        className="payment-refund-drawer"
        title="결제 취소"
        open={Boolean(cancelTarget)}
        onClose={() => !busy && setCancelTarget(null)}
        width={560}
        footer={
          <Space>
            <Button disabled={busy} onClick={() => setCancelTarget(null)}>
              취소
            </Button>
            <Button danger type="primary" loading={busy} onClick={saveCancel}>
              전체 결제 취소
            </Button>
          </Space>
        }
      >
        {actionError && <Alert type="error" message={actionError} />}
        <Form layout="vertical" disabled={busy}>
          <Form.Item label="취소 금액">
            <Input value={formatWon(cancelTarget?.payment.amount)} readOnly />
          </Form.Item>
          <Form.Item label="취소 사유">
            <Select
              value={cancelReason}
              onChange={setCancelReason}
              options={[
                "중복 결제",
                "결제 금액 오류",
                "퇴원",
                "휴원",
                "기타",
              ].map((value) => ({ value, label: value }))}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
